import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import { 
  QrCodeIcon, 
  ShoppingCartIcon,
  HeartIcon,
  MapPinIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  ArrowLeftIcon,
  BoltIcon,
  CubeIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useCart } from '../../context/CartContext';

const QRScanner = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const [scanning, setScanning] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const currentYear = new Date().getFullYear();

  // Scroll effect for glass header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // QR Scanner effect
  useEffect(() => {
    if (!scanning) return;

    const scanner = new Html5QrcodeScanner(
      'qr-reader',
      { 
        fps: 10, 
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        showTorchButtonIfSupported: true,
        showZoomSliderIfSupported: true,
        defaultZoomValueIfSupported: 2
      },
      false
    );

    scanner.render(success, error);

    function success(result) {
      scanner.clear();
      setScanning(false);
      
      // Extract fridge ID from QR code URL
      try {
        const fridgeId = result.split('/').pop();
        toast.success('✅ QR Code scanned successfully!');
        navigate(`/fridge/${fridgeId}`);
      } catch (err) {
        toast.error('Invalid QR code format');
        setScanning(true);
      }
    }

    function error(err) {
      // Silent error handling for continuous scanning
      if (err?.includes('NotFound')) {
        // No QR code found, ignore
      }
    }

    return () => {
      scanner.clear();
    };
  }, [scanning, navigate]);

  const cartCount = cart.reduce((total, item) => total + (item.quantity || 1), 0);

  const styles = `
    /* Reset & Base */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      line-height: 1.5;
      color: #1f2937;
      background-color: #f9fafb;
    }

    /* Container */
    .fh-container {
      width: 100%;
      max-width: 1280px;
      margin-left: auto;
      margin-right: auto;
      padding-left: 1rem;
      padding-right: 1rem;
    }

    @media (min-width: 640px) {
      .fh-container {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }
    }

    /* ========== GLASS HEADER (Identical to Home/Products/Cart) ========== */
    .fh-header {
      position: sticky;
      top: 0;
      z-index: 50;
      width: 100%;
      transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    }
    
    .fh-header.default {
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 4px 20px rgba(0,0,0,0.02);
      border-bottom: 1px solid rgba(229, 231, 235, 0.3);
    }
    
    .fh-header.scrolled {
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.05);
      border-bottom: 1px solid rgba(5, 150, 105, 0.15);
    }

    .fh-header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 70px;
      transition: height 0.3s ease;
    }

    @media (min-width: 768px) {
      .fh-header-content {
        height: 85px;
      }
      .fh-header.scrolled .fh-header-content {
        height: 75px;
      }
    }

    /* Logo */
    .fh-logo {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
    }

    .fh-logo img {
      height: 45px;
      width: auto;
      object-fit: contain;
      transition: transform 0.3s ease;
    }
    .fh-logo:hover img {
      transform: scale(1.05);
    }

    @media (min-width: 768px) {
      .fh-logo img {
        height: 55px;
      }
    }

    .fh-logo-text {
      display: flex;
      flex-direction: column;
    }

    .fh-logo-text h1 {
      font-size: 1.25rem;
      font-weight: 700;
      background: linear-gradient(135deg, #059669, #0891b2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.2;
    }

    @media (min-width: 768px) {
      .fh-logo-text h1 {
        font-size: 1.5rem;
      }
    }

    .fh-logo-text span {
      font-size: 0.7rem;
      color: #6b7280;
      letter-spacing: 0.5px;
    }

    /* Desktop Navigation */
    .fh-nav {
      display: none;
    }

    @media (min-width: 768px) {
      .fh-nav {
        display: flex;
        align-items: center;
        gap: 2.5rem;
      }
    }

    .fh-nav-link {
      color: #374151;
      font-weight: 500;
      text-decoration: none;
      position: relative;
      transition: color 0.2s;
      font-size: 0.95rem;
    }

    .fh-nav-link:hover {
      color: #059669;
    }

    .fh-nav-link::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: #059669;
      transition: width 0.3s;
    }

    .fh-nav-link:hover::after {
      width: 100%;
    }

    /* Buttons */
    .fh-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.6rem 1.5rem;
      border-radius: 9999px;
      font-weight: 600;
      font-size: 0.9rem;
      transition: all 0.3s;
      cursor: pointer;
      border: none;
      gap: 0.5rem;
    }

    @media (min-width: 768px) {
      .fh-btn {
        padding: 0.75rem 1.75rem;
        font-size: 1rem;
      }
    }

    .fh-btn-primary {
      background: linear-gradient(135deg, #10b981, #06b6d4);
      color: white;
      box-shadow: 0 4px 6px rgba(16,185,129,0.2);
    }

    .fh-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 15px rgba(16,185,129,0.3);
    }

    .fh-shop-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #374151;
      font-weight: 500;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem 1.25rem;
      border-radius: 9999px;
      font-size: 0.95rem;
    }

    .fh-shop-btn:hover {
      color: #059669;
      background: rgba(243, 244, 246, 0.7);
    }

    /* Cart Badge */
    .fh-cart-badge {
      position: absolute;
      top: -8px;
      right: -8px;
      background: linear-gradient(135deg, #ef4444, #dc2626);
      color: white;
      font-size: 0.75rem;
      font-weight: 600;
      height: 1.25rem;
      width: 1.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 9999px;
      border: 2px solid white;
    }

    .fh-cart-icon {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem;
      border-radius: 9999px;
      transition: all 0.2s;
      cursor: pointer;
    }
    .fh-cart-icon:hover {
      background: rgba(243, 244, 246, 0.7);
    }

    /* ========== QR SCANNER SPECIFIC STYLES ========== */
    .fh-scanner-container {
      max-width: 48rem;
      margin: 2rem auto;
      padding: 0 1rem;
    }

    .fh-scanner-card {
      background: white;
      border-radius: 2rem;
      padding: 2rem;
      box-shadow: 0 20px 35px -8px rgba(0,0,0,0.05);
      border: 1px solid rgba(229, 231, 235, 0.6);
      backdrop-filter: blur(8px);
    }

    .fh-scanner-header {
      text-align: center;
      margin-bottom: 2rem;
    }

    .fh-scanner-title {
      font-size: 2rem;
      font-weight: 800;
      background: linear-gradient(135deg, #059669, #0891b2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 0.75rem;
    }

    .fh-scanner-subtitle {
      color: #6b7280;
      font-size: 1.1rem;
    }

    /* QR Reader Container */
    #qr-reader {
      width: 100% !important;
      border: none !important;
      border-radius: 1.5rem !important;
      overflow: hidden !important;
      background: #f3f4f6 !important;
    }

    #qr-reader video {
      border-radius: 1.5rem !important;
      width: 100% !important;
      height: auto !important;
      object-fit: cover !important;
    }

    #qr-reader__scan_region {
      background: #f3f4f6 !important;
      border-radius: 1.5rem !important;
    }

    #qr-reader__dashboard {
      padding: 1rem !important;
      background: white !important;
      border-bottom-left-radius: 1.5rem !important;
      border-bottom-right-radius: 1.5rem !important;
    }

    #qr-reader__dashboard button {
      background: linear-gradient(135deg, #059669, #10b981) !important;
      color: white !important;
      border: none !important;
      padding: 0.6rem 1.25rem !important;
      border-radius: 9999px !important;
      font-weight: 600 !important;
      font-size: 0.9rem !important;
      margin: 0.25rem !important;
      transition: all 0.3s !important;
    }

    #qr-reader__dashboard button:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 8px 12px rgba(5,150,105,0.2) !important;
    }

    #qr-reader__dashboard span {
      color: #6b7280 !important;
    }

    #qr-reader__dashboard select {
      border: 2px solid #e5e7eb !important;
      border-radius: 9999px !important;
      padding: 0.5rem 2rem 0.5rem 1rem !important;
      background: white !important;
      color: #1f2937 !important;
      font-size: 0.9rem !important;
    }

    .fh-back-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      color: #4b5563;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 1rem;
      padding: 0.5rem 1rem;
      border-radius: 9999px;
      transition: all 0.2s;
      margin-bottom: 1.5rem;
    }

    .fh-back-btn:hover {
      background: #f3f4f6;
      color: #059669;
    }

    .fh-scan-instruction {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      background: #ecfdf5;
      padding: 1rem 1.5rem;
      border-radius: 9999px;
      color: #065f46;
      margin-top: 1.5rem;
    }

    .fh-scan-instruction svg {
      width: 1.25rem;
      height: 1.25rem;
      color: #059669;
    }

    /* Trust Badges */
    .fh-trust-banner {
      background: #f9fafb;
      padding: 1.5rem 0;
      border-top: 1px solid #e5e7eb;
      border-bottom: 1px solid #e5e7eb;
      margin-top: 3rem;
    }

    /* ========== FOOTER (Complete) ========== */
    .fh-footer {
      background: #0f172a;
      color: white;
      padding: 4rem 0 2rem;
      margin-top: 4rem;
    }

    .fh-footer-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 2.5rem;
      margin-bottom: 3rem;
    }

    @media (min-width: 640px) {
      .fh-footer-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (min-width: 1024px) {
      .fh-footer-grid {
        grid-template-columns: 2fr 1fr 1fr 1.5fr;
        gap: 3rem;
      }
    }

    .fh-footer-logo {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      cursor: pointer;
    }

    .fh-footer-logo img {
      height: 50px;
      width: auto;
    }

    .fh-footer-logo h3 {
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #34d399, #22d3ee);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .fh-footer-about {
      color: #94a3b8;
      line-height: 1.7;
      margin-bottom: 1.5rem;
      font-size: 0.95rem;
    }

    .fh-footer-social {
      display: flex;
      gap: 1rem;
    }

    .fh-social-icon {
      width: 2.5rem;
      height: 2.5rem;
      background: #1e293b;
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      transition: all 0.3s;
    }

    .fh-social-icon:hover {
      background: #2563eb;
      transform: translateY(-3px);
    }

    .fh-footer-title {
      font-size: 1.125rem;
      font-weight: 600;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: white;
    }

    .fh-footer-heart {
      color: #ef4444;
      animation: heartbeat 1.5s ease-in-out infinite;
    }

    .fh-footer-links {
      list-style: none;
      padding: 0;
    }

    .fh-footer-links li {
      margin-bottom: 0.875rem;
    }

    .fh-footer-links a {
      color: #94a3b8;
      text-decoration: none;
      font-size: 0.95rem;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .fh-footer-links a:hover {
      color: white;
      transform: translateX(5px);
    }

    .fh-footer-contact {
      list-style: none;
      padding: 0;
    }

    .fh-footer-contact li {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin-bottom: 1rem;
      color: #94a3b8;
      font-size: 0.95rem;
    }

    .fh-footer-contact svg {
      width: 1.25rem;
      height: 1.25rem;
      color: #94a3b8;
      flex-shrink: 0;
    }

    .fh-footer-newsletter {
      margin-top: 1.5rem;
    }

    .fh-newsletter-input {
      display: flex;
      gap: 0.5rem;
    }

    .fh-newsletter-input input {
      flex: 1;
      padding: 0.75rem 1rem;
      border-radius: 9999px;
      border: 1px solid #334155;
      background: #1e293b;
      color: white;
      font-size: 0.9rem;
    }

    .fh-newsletter-input input::placeholder {
      color: #64748b;
    }

    .fh-newsletter-btn {
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 9999px;
      padding: 0.75rem 1.25rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s;
    }

    .fh-newsletter-btn:hover {
      background: #1d4ed8;
      transform: translateY(-2px);
    }

    .fh-footer-bottom {
      border-top: 1px solid #1e293b;
      padding-top: 2rem;
      margin-top: 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    @media (min-width: 768px) {
      .fh-footer-bottom {
        flex-direction: row;
      }
    }

    .fh-copyright {
      color: #64748b;
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .fh-footer-bottom-links {
      display: flex;
      gap: 2rem;
      align-items: center;
    }

    .fh-footer-bottom-links a {
      color: #94a3b8;
      text-decoration: none;
      font-size: 0.875rem;
      transition: color 0.2s;
    }

    .fh-footer-bottom-links a:hover {
      color: white;
    }

    .fh-seo9ine-link {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      color: #f59e0b !important;
      font-weight: 700;
      text-decoration: none;
      transition: all 0.3s;
      font-size: 0.95rem;
    }

    .fh-seo9ine-link:hover {
      color: #fbbf24 !important;
      transform: scale(1.05);
    }

    @keyframes heartbeat {
      0%, 100% { transform: scale(1); }
      25% { transform: scale(1.1); }
      50% { transform: scale(1); }
      75% { transform: scale(1.1); }
    }

    .animate-heartbeat {
      animation: heartbeat 1.5s ease-in-out infinite;
    }

    .cursor-pointer {
      cursor: pointer;
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-gray-50">
        {/* Glass Header - Identical to all pages */}
        <header className={`fh-header ${scrolled ? 'scrolled' : 'default'}`}>
          <div className="fh-container">
            <div className="fh-header-content">
              {/* Logo */}
              <div className="fh-logo" onClick={() => navigate('/')}>
                <img 
                  src="/images/logo.png" 
                  alt="Fresh Hills" 
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/55?text=FH'; }}
                />
                <div className="fh-logo-text">
                  <h1>Fresh Hills</h1>
                  <span>Fresh & Healthy Since 2024</span>
                </div>
              </div>

              {/* Desktop Navigation */}
              <nav className="fh-nav">
                <a href="/" className="fh-nav-link">Home</a>
                <a href="/#features" className="fh-nav-link">Features</a>
                <a href="/#offers" className="fh-nav-link">Offers</a>
                <a href="/#how-it-works" className="fh-nav-link">How It Works</a>
                <a href="/#contact" className="fh-nav-link">Contact</a>
              </nav>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 md:gap-3">
                <button 
                  onClick={() => navigate('/products')}
                  className="fh-shop-btn"
                >
                  <span className="hidden sm:inline">Shop</span>
                  <ShoppingCartIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => navigate('/scan')}
                  className="fh-btn fh-btn-primary"
                >
                  <QrCodeIcon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="hidden sm:inline">Scan & Shop</span>
                  <span className="sm:hidden">Scan</span>
                </button>
                {/* Cart Icon with Badge */}
                <div className="fh-cart-icon" onClick={() => navigate('/cart')}>
                  <ShoppingCartIcon className="h-6 w-6 text-gray-600" />
                  {cartCount > 0 && (
                    <span className="fh-cart-badge">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="fh-scanner-container">
          {/* Back Button */}
          <button onClick={() => navigate(-1)} className="fh-back-btn">
            <ArrowLeftIcon className="w-5 h-5" />
            Back
          </button>

          {/* Scanner Card */}
          <div className="fh-scanner-card">
            <div className="fh-scanner-header">
              <h1 className="fh-scanner-title">Scan QR Code</h1>
              <p className="fh-scanner-subtitle">
                Find a Fresh Hills smart fridge and scan the QR code
              </p>
            </div>

            {/* QR Reader */}
            <div id="qr-reader" className="w-full"></div>

            {/* Instruction */}
            <div className="fh-scan-instruction">
              <QrCodeIcon />
              <span className="flex-1 text-sm font-medium">
                Point your camera at the QR code on the fridge door
              </span>
              <SparklesIcon className="w-5 h-5 text-emerald-500" />
            </div>

            {/* Manual Entry Option */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500 mb-3">Or enter fridge ID manually</p>
              <div className="flex items-center justify-center gap-3">
                <input
                  type="text"
                  placeholder="Enter fridge ID (e.g., FH24)"
                  className="px-4 py-2 border-2 border-gray-200 rounded-full focus:border-emerald-500 focus:outline-none text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      navigate(`/fridge/${e.target.value}`);
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('input[placeholder="Enter fridge ID (e.g., FH24)"]');
                    if (input?.value) {
                      navigate(`/fridge/${input.value}`);
                    }
                  }}
                  className="fh-btn fh-btn-primary"
                >
                  Go
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges Bar */}
        <div className="fh-trust-banner">
          <div className="fh-container">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              <div className="flex items-center gap-2">
                <div style={{ background: '#d1fae5', padding: '0.5rem', borderRadius: '9999px' }}>
                  <BoltIcon className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">Same Day Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ background: '#dbeafe', padding: '0.5rem', borderRadius: '9999px' }}>
                  <CurrencyRupeeIcon className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">Cashless Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ background: '#fef3c7', padding: '0.5rem', borderRadius: '9999px' }}>
                  <CubeIcon className="w-4 h-4 text-amber-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">Fresh Stock Daily</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ background: '#fae8ff', padding: '0.5rem', borderRadius: '9999px' }}>
                  <UserGroupIcon className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">10k+ Customers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Complete */}
        <footer className="fh-footer">
          <div className="fh-container">
            <div className="fh-footer-grid">
              {/* Brand Column */}
              <div>
                <div className="fh-footer-logo" onClick={() => navigate('/')}>
                  <img 
                    src="/images/logo.png" 
                    alt="Fresh Hills" 
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/50?text=FH'; }}
                  />
                  <h3>Fresh Hills</h3>
                </div>
                <p className="fh-footer-about">
                  India's first smart fridge network bringing fresh dairy products to your doorstep 24/7. 
                  No queues, no cashiers, just pure convenience since 2024.
                </p>
                <div className="fh-footer-social">
                  <a href="#" className="fh-social-icon">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a href="#" className="fh-social-icon">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
                    </svg>
                  </a>
                  <a href="#" className="fh-social-icon">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="fh-footer-title">
                  <HeartIcon className="w-5 h-5 fh-footer-heart" />
                  Quick Links
                </h4>
                <ul className="fh-footer-links">
                  <li><a href="/"><ChevronRightIcon className="w-3 h-3" /> Home</a></li>
                  <li><a href="/#features"><ChevronRightIcon className="w-3 h-3" /> Features</a></li>
                  <li><a href="/#offers"><ChevronRightIcon className="w-3 h-3" /> Offers</a></li>
                  <li><a href="/#how-it-works"><ChevronRightIcon className="w-3 h-3" /> How It Works</a></li>
                  <li><a href="/#contact"><ChevronRightIcon className="w-3 h-3" /> Contact</a></li>
                </ul>
              </div>

              {/* Products */}
              <div>
                <h4 className="fh-footer-title">
                  <HeartIcon className="w-5 h-5 fh-footer-heart" />
                  Our Products
                </h4>
                <ul className="fh-footer-links">
                  <li><a href="/products?category=milk"><ChevronRightIcon className="w-3 h-3" /> Fresh Milk</a></li>
                  <li><a href="/products?category=butter"><ChevronRightIcon className="w-3 h-3" /> Butter & Cream</a></li>
                  <li><a href="/products?category=cheese"><ChevronRightIcon className="w-3 h-3" /> Cheese</a></li>
                  <li><a href="/products?category=eggs"><ChevronRightIcon className="w-3 h-3" /> Farm Eggs</a></li>
                  <li><a href="/products?category=yogurt"><ChevronRightIcon className="w-3 h-3" /> Curd & Yogurt</a></li>
                </ul>
              </div>

              {/* Contact & Newsletter */}
              <div>
                <h4 className="fh-footer-title">
                  <HeartIcon className="w-5 h-5 fh-footer-heart" />
                  Contact Us
                </h4>
                <ul className="fh-footer-contact">
                  <li>
                    <MapPinIcon />
                    <span>#245, 2nd Main, Indiranagar, Bangalore - 560038</span>
                  </li>
                  <li>
                    <DevicePhoneMobileIcon />
                    <span>+91 98765 43210</span>
                  </li>
                  <li>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>hello@freshhills.com</span>
                  </li>
                </ul>

                <div className="fh-footer-newsletter">
                  <h5 style={{ color: 'white', fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Subscribe for offers
                  </h5>
                  <div className="fh-newsletter-input">
                    <input type="email" placeholder="Your email address" />
                    <button className="fh-newsletter-btn">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="fh-footer-bottom">
              <div className="fh-copyright">
                <span>© {currentYear} Fresh Hills. All rights reserved.</span>
                <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>❤️</span>
                <span style={{ marginLeft: '0.25rem' }}>Made in India</span>
              </div>
              <div className="fh-footer-bottom-links">
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
                <a href="#">Cookie Policy</a>
                <a 
                  href="https://seo9ine.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="fh-seo9ine-link"
                >
                  <span>SEO9INE</span>
                  <HeartIcon className="w-4 h-4 animate-heartbeat" style={{ color: '#ef4444' }} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default QRScanner;